/**
 * Pricing admin WRITE COMMANDS — B2.5a' (fullsuite plan §B2.5a').
 *
 * THE shared write semantics. Each command is guard-then-apply over an
 * immutable `PricingDataset`: it either throws a `PricingAdminError` and
 * changes NOTHING, or returns a new dataset. The MSW handlers, the FE store
 * and the GraphQL server all call these — a rule can therefore not be
 * enforced in one world and skipped in another, which is the whole point of
 * spec-by-mock-first (the mock IS the executable spec the server is built
 * against).
 *
 * Pure by construction: no id minting, no clocks, no audit-row authoring.
 * Ids and timestamps are the CALLER's (MSW mints mock ids, Postgres mints
 * real ones); the PRICE_OVERRIDE audit row is likewise the caller's — the
 * store appends it in-memory, the server writes it to the audit spine. What
 * IS shared is the reason gate that makes the audit row non-optional.
 *
 * The apply-half of each command is the S9.1/S4.1 store reducer moved here
 * VERBATIM (B0.2' extraction doctrine — `src/stores/pricingStore.ts` now
 * re-exports these names, so every existing importer keeps resolving).
 */

import type {
  CostRecord,
  CurrencyPriceRow,
  CustomerGroupDiscount,
  CustomerPricing,
  MarkupRule,
  PriceGroupBand,
  PriceLevel,
  PricingDataset,
  PricingGroup,
  ProductPricingFields,
  QuantityPricingSchedule,
  SpecialItemPrice,
} from '@/types/api/pricing';
import { PricingAdminError } from './errors';
import {
  findCurrencyCellRow,
  guardCostRecordPatch,
  guardCurrencyPriceRowSave,
  guardCustomerGroupDiscountSave,
  guardCustomerPricingSave,
  guardMarkupRuleEdit,
  guardPriceGroupBandSave,
  guardPriceLevelSave,
  guardPricingGroupSave,
  guardProductPricingSave,
  guardQuantityScheduleSave,
  guardReason,
  guardSpecialPriceSave,
  type CostRecordPatch,
  type MarkupRuleEdit,
} from './rules';

/** Throw the guard's rejection, if any (the ONE spelling of guard-then-apply). */
function refuse(error: PricingAdminError | null): void {
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Pure reducers (moved VERBATIM from src/stores/pricingStore.ts, S9.1 + S4.1)
// ---------------------------------------------------------------------------

/**
 * Apply one Group×Source×Level markup edit: update in place, create when the
 * cell/level pair has no rule yet, remove on `markupPct: null`.
 */
export function applyMarkupRuleEdit(dataset: PricingDataset, edit: MarkupRuleEdit): PricingDataset {
  const existing = dataset.markupRules.find(
    (rule) =>
      rule.priceGroupId === edit.priceGroupId &&
      rule.source === edit.source &&
      rule.priceLevelId === edit.priceLevelId,
  );
  if (edit.markupPct === null) {
    if (!existing) return dataset;
    return {
      ...dataset,
      markupRules: dataset.markupRules.filter((rule) => rule.id !== existing.id),
    };
  }
  if (existing) {
    return {
      ...dataset,
      markupRules: dataset.markupRules.map((rule) =>
        rule.id === existing.id ? { ...rule, markupPct: edit.markupPct as number } : rule,
      ),
    };
  }
  const created: MarkupRule = {
    id: `mr-${edit.priceGroupId}-${edit.source.toLowerCase()}-${edit.priceLevelId}`,
    priceGroupId: edit.priceGroupId,
    source: edit.source,
    priceLevelId: edit.priceLevelId,
    markupPct: edit.markupPct,
  };
  return { ...dataset, markupRules: [...dataset.markupRules, created] };
}

/** Append a user-defined price level (unlimited — never a hardcoded enum). */
export function applyPriceLevelAdd(dataset: PricingDataset, level: PriceLevel): PricingDataset {
  return { ...dataset, priceLevels: [...dataset.priceLevels, level] };
}

/** Upsert a price level by id (the add path stays exported for the S9.1 UI). */
export function applyPriceLevelSave(dataset: PricingDataset, level: PriceLevel): PricingDataset {
  const exists = dataset.priceLevels.some((candidate) => candidate.id === level.id);
  return exists
    ? {
        ...dataset,
        priceLevels: dataset.priceLevels.map((candidate) =>
          candidate.id === level.id ? level : candidate,
        ),
      }
    : applyPriceLevelAdd(dataset, level);
}

/** Upsert a half-open price group band by id. */
export function applyPriceGroupBandSave(
  dataset: PricingDataset,
  band: PriceGroupBand,
): PricingDataset {
  const exists = dataset.priceGroupBands.some((candidate) => candidate.id === band.id);
  return {
    ...dataset,
    priceGroupBands: exists
      ? dataset.priceGroupBands.map((candidate) => (candidate.id === band.id ? band : candidate))
      : [...dataset.priceGroupBands, band],
  };
}

/** Upsert a special price row (both fixed and override-% variants). */
export function applySpecialPriceSave(
  dataset: PricingDataset,
  row: SpecialItemPrice,
): PricingDataset {
  const exists = dataset.specialItemPrices.some((candidate) => candidate.id === row.id);
  return {
    ...dataset,
    specialItemPrices: exists
      ? dataset.specialItemPrices.map((candidate) => (candidate.id === row.id ? row : candidate))
      : [...dataset.specialItemPrices, row],
  };
}

export function applyCostRecordPatch(
  dataset: PricingDataset,
  recordId: string,
  patch: CostRecordPatch,
): PricingDataset {
  return {
    ...dataset,
    costRecords: dataset.costRecords.map((record) =>
      record.id === recordId ? ({ ...record, ...patch } as CostRecord) : record,
    ),
  };
}

/** Upsert the customer's price-level assignment, keyed by customerId. */
export function applyCustomerPricingSave(
  dataset: PricingDataset,
  row: CustomerPricing,
): PricingDataset {
  const exists = dataset.customerPricing.some(
    (candidate) => candidate.customerId === row.customerId,
  );
  return {
    ...dataset,
    customerPricing: exists
      ? dataset.customerPricing.map((candidate) =>
          candidate.customerId === row.customerId ? row : candidate,
        )
      : [...dataset.customerPricing, row],
  };
}

/** Upsert a quantity pricing schedule (by id). */
export function applyQuantityScheduleSave(
  dataset: PricingDataset,
  schedule: QuantityPricingSchedule,
): PricingDataset {
  const list = dataset.quantityPricingSchedules ?? [];
  const exists = list.some((candidate) => candidate.id === schedule.id);
  return {
    ...dataset,
    quantityPricingSchedules: exists
      ? list.map((candidate) => (candidate.id === schedule.id ? schedule : candidate))
      : [...list, schedule],
  };
}

/** Append a pricing group (unlimited user-defined rows — never an enum). */
export function applyPricingGroupAdd(dataset: PricingDataset, group: PricingGroup): PricingDataset {
  return { ...dataset, pricingGroups: [...(dataset.pricingGroups ?? []), group] };
}

/** Upsert a pricing group by id. */
export function applyPricingGroupSave(
  dataset: PricingDataset,
  group: PricingGroup,
): PricingDataset {
  const list = dataset.pricingGroups ?? [];
  const exists = list.some((candidate) => candidate.id === group.id);
  return exists
    ? {
        ...dataset,
        pricingGroups: list.map((candidate) => (candidate.id === group.id ? group : candidate)),
      }
    : applyPricingGroupAdd(dataset, group);
}

/**
 * Upsert a customer×group discount card. Keyed by the (customerId, groupId)
 * PAIR, not the row id — Discribe-20: a customer holds at most ONE card per
 * group, so saving a card replaces any existing card for the same pair.
 */
export function applyCustomerGroupDiscountSave(
  dataset: PricingDataset,
  cardRow: CustomerGroupDiscount,
): PricingDataset {
  const list = dataset.customerGroupDiscounts ?? [];
  const remaining = list.filter(
    (candidate) =>
      candidate.id !== cardRow.id &&
      !(candidate.customerId === cardRow.customerId && candidate.groupId === cardRow.groupId),
  );
  return { ...dataset, customerGroupDiscounts: [...remaining, cardRow] };
}

/**
 * Upsert a manually-locked per-currency price row, keyed by the matrix CELL
 * (productId, currency, priceLevelId) so the exact-match lookup in
 * `currencyMatrix.ts` never sees two rows for one cell.
 */
export function applyCurrencyPriceRowSave(
  dataset: PricingDataset,
  priceRow: CurrencyPriceRow,
): PricingDataset {
  const list = dataset.currencyPriceRows ?? [];
  const existing = findCurrencyCellRow(list, priceRow);
  return {
    ...dataset,
    currencyPriceRows: existing
      ? list.map((candidate) => (candidate.id === existing.id ? priceRow : candidate))
      : [...list, priceRow],
  };
}

/** Upsert the per-product pricing fields, keyed by productId. */
export function applyProductPricingSave(
  dataset: PricingDataset,
  fields: ProductPricingFields,
): PricingDataset {
  const list = dataset.productPricing ?? [];
  const exists = list.some((candidate) => candidate.productId === fields.productId);
  return {
    ...dataset,
    productPricing: exists
      ? list.map((candidate) => (candidate.productId === fields.productId ? fields : candidate))
      : [...list, fields],
  };
}

// ---------------------------------------------------------------------------
// Commands (guard → apply). Reason-gated where the save moves a PRICE.
// ---------------------------------------------------------------------------

/**
 * Which saves are price-affecting, i.e. reason-gated + PRICE_OVERRIDE-audited
 * (S9.1/S4.1): special prices, currency rows, quantity schedules, group
 * discount cards, product pricing fields, cost-record edits and markup-matrix
 * edits. Creating an empty container (a price LEVEL, a pricing GROUP) is not
 * price-affecting until a rule references it — mirrors `addPriceLevel` /
 * `addPricingGroup`, which never took a reason.
 */
export const PRICE_AFFECTING_SAVES = [
  'special_price',
  'currency_price',
  'quantity_schedule',
  'customer_group_discount',
  'product_pricing',
  'cost_record',
  'markup_rule',
  'customer_pricing',
] as const;

export type PriceAffectingSave = (typeof PRICE_AFFECTING_SAVES)[number];

export function savePriceLevel(dataset: PricingDataset, level: PriceLevel): PricingDataset {
  refuse(guardPriceLevelSave(dataset.priceLevels, level));
  return applyPriceLevelSave(dataset, level);
}

export function savePriceGroupBand(
  dataset: PricingDataset,
  band: PriceGroupBand,
): PricingDataset {
  refuse(guardPriceGroupBandSave(dataset.priceGroupBands, band));
  return applyPriceGroupBandSave(dataset, band);
}

/** Markup edits move prices — reason-gated (the matrix IS the level price). */
export function saveMarkupRuleEdits(
  dataset: PricingDataset,
  edits: readonly MarkupRuleEdit[],
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  let next = dataset;
  for (const edit of edits) {
    refuse(guardMarkupRuleEdit(next, edit));
    next = applyMarkupRuleEdit(next, edit);
  }
  return next;
}

export function saveSpecialPrice(
  dataset: PricingDataset,
  row: SpecialItemPrice,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardSpecialPriceSave(row));
  // The accepted reason IS the stored reason — a row can never carry a reason
  // the audit row did not record (anti-175%).
  return applySpecialPriceSave(dataset, { ...row, reason: reason.trim() });
}

export function setSpecialPriceActive(
  dataset: PricingDataset,
  id: string,
  isActive: boolean,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  const row = dataset.specialItemPrices.find((candidate) => candidate.id === id);
  if (!row) throw new PricingAdminError('RECORD_NOT_FOUND', 'input.id');
  return applySpecialPriceSave(dataset, { ...row, isActive });
}

export function saveCustomerPricing(
  dataset: PricingDataset,
  row: CustomerPricing,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardCustomerPricingSave(dataset, row));
  return applyCustomerPricingSave(dataset, row);
}

export function saveCostRecordPatch(
  dataset: PricingDataset,
  recordId: string,
  patch: CostRecordPatch & Record<string, unknown>,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardCostRecordPatch(dataset.costRecords, recordId, patch));
  return applyCostRecordPatch(dataset, recordId, patch);
}

export function saveQuantitySchedule(
  dataset: PricingDataset,
  schedule: QuantityPricingSchedule,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardQuantityScheduleSave(dataset.quantityPricingSchedules ?? [], schedule));
  return applyQuantityScheduleSave(dataset, schedule);
}

export function savePricingGroup(dataset: PricingDataset, group: PricingGroup): PricingDataset {
  refuse(guardPricingGroupSave(dataset.pricingGroups ?? [], group));
  return applyPricingGroupSave(dataset, group);
}

export function saveCustomerGroupDiscount(
  dataset: PricingDataset,
  cardRow: CustomerGroupDiscount,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardCustomerGroupDiscountSave(dataset, cardRow));
  return applyCustomerGroupDiscountSave(dataset, cardRow);
}

export function saveCurrencyPriceRow(
  dataset: PricingDataset,
  priceRow: CurrencyPriceRow,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardCurrencyPriceRowSave(dataset, priceRow));
  return applyCurrencyPriceRowSave(dataset, priceRow);
}

export function saveProductPricing(
  dataset: PricingDataset,
  fields: ProductPricingFields,
  reason: string,
): PricingDataset {
  refuse(guardReason(reason));
  refuse(guardProductPricingSave(dataset, fields));
  return applyProductPricingSave(dataset, fields);
}
